import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const APP_URL = "https://app.engagedent.ai";

  const plans = [
    {
      name: "Free Starter",
      price: "0",
      period: "forever",
      desc: "Perfect for trying out the platform.",
      features: [
        "3 Video Credits per month",
        "Basic Avatar Library",
        "Standard Script Generation",
        "Watermarked Videos",
        "720p Export Quality"
      ],
      cta: "Sign Up Free",
      highlighted: false,
      link: APP_URL
    },
    {
      name: "Basic",
      price: billingCycle === 'monthly' ? "49" : "39",
      period: "per month",
      desc: "For growing practices building a presence.",
      features: [
        "15 Video Credits per month",
        "Full Avatar Library access",
        "No Watermarks",
        "1080p Export Quality",
        "Auto-posting to 3 platforms",
        "Priority Rendering"
      ],
      cta: "Get Basic",
      highlighted: true,
      link: `${APP_URL}/signup?plan=basic&cycle=${billingCycle}`
    },
    {
      name: "Studio",
      price: billingCycle === 'monthly' ? "99" : "79",
      period: "per month",
      desc: "Ultimate customization and volume.",
      features: [
        "50 Video Credits per month",
        "Custom B-Roll Uploads",
        "AI B-Roll Generation",
        "Custom Brand Kits",
        "Dedicated Account Manager",
        "4K Export Quality",
        "All Social Platforms included"
      ],
      cta: "Get Studio",
      highlighted: false,
      link: `${APP_URL}/signup?plan=studio&cycle=${billingCycle}`
    }
  ];

  return (
    <div className="bg-slate-50 min-h-screen py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold text-slate-900 mb-4"
          >
            Simple, Transparent Pricing
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-slate-600 mb-8"
          >
            Choose the plan that fits your practice needs.
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex bg-white p-1 rounded-xl shadow-sm border border-slate-200"
          >
            <button 
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-lg text-sm font-semibold transition-all ${billingCycle === 'monthly' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              Monthly
            </button>
            <button 
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-lg text-sm font-semibold transition-all ${billingCycle === 'yearly' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              Yearly <span className="text-xs text-green-400 font-normal ml-1">(-20%)</span>
            </button>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className={`relative bg-white rounded-2xl p-8 border ${plan.highlighted ? 'border-cyan-500 shadow-2xl scale-105 z-10' : 'border-slate-200 shadow-lg'} flex flex-col`}
            >
              {plan.highlighted && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-cyan-500 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                  Most Popular
                </div>
              )}
              
              <div className="mb-8">
                <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
                <p className="text-slate-500 text-sm mt-2">{plan.desc}</p>
                <div className="mt-6 flex items-baseline">
                  <span className="text-4xl font-extrabold text-slate-900">${plan.price}</span>
                  <span className="text-slate-500 ml-2">/{plan.period}</span>
                </div>
              </div>

              <ul className="space-y-4 mb-8 flex-grow">
                {plan.features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                    <SafeIcon icon={FiIcons.FiCheck} className={`mt-0.5 flex-shrink-0 ${plan.highlighted ? 'text-cyan-500' : 'text-slate-400'}`} />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>

              <a href={plan.link}>
                <button className={`w-full py-4 rounded-xl font-bold transition-all ${plan.highlighted ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-cyan-500/30 shadow-lg' : 'bg-slate-100 text-slate-900 hover:bg-slate-200'}`}>
                  {plan.cta}
                </button>
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Pricing;